/**
 * Structured data for a docs page: a `TechArticle` plus a `BreadcrumbList`
 * matching the visible breadcrumb rendered by `DocsShell`. Pure and
 * pathname-driven (no React) so it can be unit tested against every entry
 * in `DOC_SECTIONS` without rendering anything — see docs-json-ld.test.ts,
 * which fails the moment a new doc page ships without a `flatDocs()` entry.
 */
import {
  DOCS_BASE,
  docsIndexLastModified,
  flatDocs,
  getBreadcrumb,
} from "@/app/(marketing)/docs/_config";
import { ORGANIZATION_ID } from "@/lib/json-ld";

const SITE_URL = "https://raileon.com";

export type DocJsonLd = {
  techArticle: Record<string, unknown>;
  breadcrumb: Record<string, unknown>;
};

const DOCS_INDEX_DESCRIPTION =
  "Step-by-step guides for setting up Raileon, connecting your tools, and getting the most out of your AI agents.";

export function buildDocJsonLd(pathname: string): DocJsonLd | null {
  // The /docs index isn't a `flatDocs()` entry (only its child pages are),
  // so it needs its own single-item breadcrumb instead of going through
  // the section/page lookup below.
  if (pathname === DOCS_BASE) {
    const url = `${SITE_URL}${DOCS_BASE}`;
    return {
      techArticle: {
        "@context": "https://schema.org",
        "@type": "TechArticle",
        headline: "Raileon Documentation",
        description: DOCS_INDEX_DESCRIPTION,
        dateModified: docsIndexLastModified(),
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        author: { "@id": ORGANIZATION_ID },
        publisher: { "@id": ORGANIZATION_ID },
      },
      breadcrumb: {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Docs", item: url },
        ],
      },
    };
  }

  const doc = flatDocs().find((d) => d.path === pathname);
  const crumb = getBreadcrumb(pathname);
  if (!doc || !crumb) return null;

  const url = `${SITE_URL}${doc.path}`;

  const techArticle: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: doc.title,
    description: doc.description ?? doc.title,
    // Intentionally the bare `_config.ts` value (e.g. "2026-09-24"), not a
    // full timestamp — a plain YYYY-MM-DD is valid ISO 8601 and it lets a
    // page's rendered dateModified be compared byte-for-byte against the
    // single source of truth in _config.ts.
    dateModified: doc.lastModified,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    author: { "@id": ORGANIZATION_ID },
    publisher: { "@id": ORGANIZATION_ID },
  };

  const breadcrumb: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Docs",
        item: `${SITE_URL}${DOCS_BASE}`,
      },
      {
        "@type": "ListItem",
        position: 2,
        // The section grouping (e.g. "Getting Started") has no page of its
        // own to link to — only its child pages are routable — so this
        // entry carries a name and no `item`, matching the unlinked
        // section label in the visible breadcrumb.
        name: crumb.sectionTitle,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: crumb.pageTitle,
        item: url,
      },
    ],
  };

  return { techArticle, breadcrumb };
}
